import { ChangeEvent, FC } from 'react';
import {
    Button,
    ButtonStyles,
    Checkbox,
    Icons,
    RadioGroup,
    TextInput,
    TextInputSizes,
    Validation,
    ValidationStatus
} from '@silverfort/sf-components';
import { ROLES } from '@silverfort-nodelibs/common/utils/consts';
import { Controller, ControllerProps, useFormContext, useWatch } from 'react-hook-form';
import { UserDrawerFormValues } from '../../../types/forms';
import { isEmailValid } from '../../../../utils';
import { isUserPermitted } from '../../../../../TowerMenu/TowerMenuUtils';
import { UserEmailType } from '../../SendEnrollmentRequest/types';
import { ProtectedEntityType, UserSession } from '../../../../../api';
import useSendEnrollmentRequestPopup from '../../SendEnrollmentRequest/hooks/useSendEnrollmentRequestPopup';
import { SystemData } from '../../../types/SystemData';

interface Props {
    adEmail?: string;
    userGuid: string;
    userSession: UserSession;
    systemData?: SystemData;
}

const EMAIL_TYPE_OPTIONS = [
    { title: 'AD default', value: UserEmailType.AD },
    { title: 'Email', value: UserEmailType.CUSTOM }
];

const EMAIL_RULES: ControllerProps<UserDrawerFormValues, 'email'>['rules'] = {
    validate: (email, { emailType }) => {
        const valid = (!!email && isEmailValid(email)) || emailType === UserEmailType.AD;

        if (!valid) {
            return 'The email you entered is invalid.';
        }

        return valid;
    }
};

const Email: FC<Props> = ({ adEmail = '', userSession, userGuid, systemData }) => {
    const {
        control,
        setValue,
        trigger,
        formState: { errors }
    } = useFormContext<UserDrawerFormValues>();

    const { openSendEnrollmentRequestPopup } = useSendEnrollmentRequestPopup({
        systemData,
        userSession,
        recipients: [{ guid: userGuid, type: ProtectedEntityType.USER }]
    });

    const userEmailType = useWatch({ control, name: 'emailType' });

    const isADDefaultEmail = userEmailType === UserEmailType.AD;

    const sendDisabled = !errors.email && !isADDefaultEmail;

    const handleEmailTypeChange = (value: UserEmailType) => {
        setValue('emailType', value, { shouldDirty: true });
        setValue('email', '', { shouldDirty: true });
        trigger('email');
    };

    const handleSetIsExternalEmail = (event: ChangeEvent<HTMLInputElement>) => {
        const { checked } = event.target;
        setValue('isExternalEmail', checked, { shouldDirty: true });
    };

    return (
        <div className="rounded-[8px] border border-sf-gray-100">
            <div className="px-4 h-14 flex items-center border-b border-sf-gray-100">
                <span className="font-sf font-medium text-13 leading-[15.87px]">Email</span>
            </div>
            <div className="p-4 flex flex-col gap-2">
                <div className="flex items-center gap-2">
                    <Controller
                        name="emailType"
                        control={control}
                        render={({ field: { value } }) => (
                            <RadioGroup
                                selectedOption={value}
                                options={EMAIL_TYPE_OPTIONS}
                                //@ts-ignore TODO: this issue might be solved in the components library since it doesnt know how to get generics (and we want type of closed values here)
                                onChange={handleEmailTypeChange}
                            />
                        )}
                    />

                    <Controller
                        name="email"
                        control={control}
                        rules={EMAIL_RULES}
                        render={({ field: { value, onChange, onBlur }, fieldState: { error, isTouched } }) => (
                            <div className="flex flex-col gap-1 max-h-8">
                                <TextInput
                                    disabled={
                                        isADDefaultEmail ||
                                        !isUserPermitted(userSession, 'setEmail', ROLES.EDITOR.value)
                                    }
                                    inputSize={TextInputSizes.Small}
                                    value={isADDefaultEmail ? '' : value}
                                    placeholder={adEmail || 'Email address'}
                                    onChange={onChange}
                                    onBlur={onBlur}
                                />
                                {error && isTouched && (
                                    <Validation
                                        showIcon={false}
                                        text={error.message || ''}
                                        validationStatus={ValidationStatus.Error}
                                    />
                                )}
                            </div>
                        )}
                    />
                </div>
                <div className="flex items-center justify-between">
                    <Controller
                        name="isExternalEmail"
                        control={control}
                        render={({ field: { value } }) => (
                            <Checkbox
                                label="Mark as external email"
                                checked={value}
                                onChange={handleSetIsExternalEmail}
                            />
                        )}
                    />
                    <Button
                        text="Send"
                        disabled={
                            sendDisabled || !isUserPermitted(userSession, 'sendEnrollmentEmail', ROLES.EDITOR.value)
                        }
                        onClick={openSendEnrollmentRequestPopup}
                        buttonStyle={ButtonStyles.Primary}
                        iconLeft={Icons.Mail}
                    />
                </div>
            </div>
        </div>
    );
};

export default Email;
